import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const GmailSetup: React.FC = () => {
  const [gmailConfig, setGmailConfig] = useState({
    email: '',
    appPassword: '',
    pollingEnabled: true,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.gmailConfig) {
      setGmailConfig({
        email: user.gmailConfig.email || '',
        appPassword: '',
        pollingEnabled: user.gmailConfig.pollingEnabled !== false,
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/gmail/configure`,
        gmailConfig,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data) {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Configuration failed');
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/gmail/test-connection`,
        {
          email: gmailConfig.email,
          appPassword: gmailConfig.appPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.data.success) {
        alert('Connection successful! Your Gmail account is properly configured.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setGmailConfig({
      ...gmailConfig,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Setup Gmail</h2>
          <p className="mt-2 text-gray-600">
            Configure your Gmail account for todo management
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2">
            How to get an App Password:
          </h3>
          <ol className="text-sm text-blue-800 space-y-1">
            <li>1. Go to your Google Account settings</li>
            <li>2. Select Security → 2-Step Verification</li>
            <li>3. At the bottom, choose App passwords</li>
            <li>4. Generate a password for "Mail"</li>
            <li>5. Use that 16-character password below</li>
          </ol>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gmail Address
            </label>
            <input
              name="email"
              type="email"
              required
              placeholder="your.email@gmail.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={gmailConfig.email}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              App Password
            </label>
            <input
              name="appPassword"
              type="password"
              required
              placeholder="Enter app password (spaces OK)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
              value={gmailConfig.appPassword}
              onChange={handleChange}
            />
          </div>

          <div className="flex items-center">
            <input
              name="pollingEnabled"
              type="checkbox"
              className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              checked={gmailConfig.pollingEnabled}
              onChange={handleChange}
            />
            <label className="ml-2 text-sm text-gray-700">
              Enable automatic email checking (recommended)
            </label>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={testConnection}
              disabled={
                !gmailConfig.email || !gmailConfig.appPassword || testingConnection
              }
              className="flex-1 bg-gray-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testingConnection ? 'Testing...' : 'Test Connection'}
            </button>

            <button
              type="submit"
              disabled={
                loading || !gmailConfig.email || !gmailConfig.appPassword
              }
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save & Continue'}
            </button>
          </div>

          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full text-gray-600 hover:text-gray-800 text-sm"
          >
            Skip for now (you can configure later)
          </button>
        </form>
      </div>
    </div>
  );
};

export default GmailSetup;